<script setup lang="ts">
import CitationCard from '~/components/citation/CitationCard.vue'
import type { CitationSnippet, CitationTarget } from '~/components/citation/types'
import { citationFragmentIndex, useCitationAnchor, type PointerPosition } from '~/composables/useCitationAnchor'
import { useHoverIntent } from '~/composables/useHoverIntent'

const props = defineProps<{
  /** Map of sectionId → snippet data, populated from tool_result + the /api/legal/read resolver. */
  snippets: Map<string, CitationSnippet>
  /** Id of the assistant message currently streaming (null when idle). */
  streamingMessageId?: string | null
  /** True while missing snippets are being fetched from the API. */
  citationsResolving?: boolean
}>()

const emit = defineEmits<{
  navigate: [normId: string | null, sectionId: string, source?: { messageId: string }]
}>()

const floatingRef = ref<HTMLElement | null>(null)
const { target, openedBy, open, close, scheduleClose, cancelClose } = useHoverIntent<CitationTarget>()
const { floatingStyles } = useCitationAnchor(target, floatingRef)

// Resolve the snippet REACTIVELY from the shared map (not snapshotted at hover time), so a snippet that
// arrives while the card is open flips it from spinner → content without a re-hover.
const currentSnippet = computed<CitationSnippet | null>(() =>
  target.value ? props.snippets.get(target.value.sectionId) ?? null : null)

// Spinner state: the snippet isn't loaded yet AND it's still expected to arrive — either ITS message is
// streaming (per-message, not chat-global) or the API resolver is in flight. Otherwise it's genuinely
// unavailable → the "Cita legal" fallback.
const isLoading = computed(() => {
  if (!target.value || currentSnippet.value) return false
  const streamingThisMessage = !!props.streamingMessageId && target.value.messageId === props.streamingMessageId
  return streamingThisMessage || !!props.citationsResolving
})

function navigateTo(snippet: CitationSnippet, messageId?: string) {
  emit('navigate', snippet.normId || null, snippet.sectionId, messageId ? { messageId } : undefined)
  close()
}

function handleCardNavigate() {
  const snippet = currentSnippet.value
  if (snippet?.sectionId) navigateTo(snippet, target.value?.messageId)
}

// ── Event delegation on the transcript container ──
function citeFrom(e: Event): HTMLElement | null {
  return ((e.target as HTMLElement).closest?.('.marcus-cite') as HTMLElement | null) ?? null
}

function targetFrom(el: HTMLElement, pointer?: PointerPosition | null): CitationTarget {
  return {
    el,
    sectionId: el.dataset.sectionId || '',
    label: el.dataset.label || '',
    // Carry the owning message id (A5) so the law view can offer "return to the answer".
    messageId: el.closest('[data-message-id]')?.getAttribute('data-message-id') || undefined,
    fragmentIndex: citationFragmentIndex(el, pointer),
  }
}

function setupListeners(container: HTMLElement) {
  // Click: open the full article (A2) — the same navigation the card's "Ver artículo completo"
  // performs — so a click doesn't merely toggle the preview card. Hover/focus still show the card.
  container.addEventListener('click', (e) => {
    const el = citeFrom(e)
    if (!el) return
    e.preventDefault()
    const sectionId = el.dataset.sectionId || ''
    const snippet = props.snippets.get(sectionId) || null
    if (snippet?.sectionId) {
      navigateTo(snippet, el.closest('[data-message-id]')?.getAttribute('data-message-id') || undefined)
      return
    }
    // Unresolved citation (snippet not loaded) → fall back to toggling the hover card so the user
    // still gets an affordance instead of a dead click. Only a card opened by a previous CLICK may be
    // toggled shut here; one opened by hover/focus (which touch fires as part of this same tap) must
    // stay open, otherwise the tap cancels itself and reads as broken.
    if (target.value?.sectionId === sectionId && openedBy.value === 'click') close()
    else open(targetFrom(el, { x: e.clientX, y: e.clientY }), 'click')
  })

  // Hover preview is for POINTER devices only. Touch browsers synthesize mouseenter before click, so
  // on a phone the sequence was: mouseenter opens the card → click sees it already open and closes it
  // again → the tap appeared to do nothing (only visible on citations whose snippet hasn't resolved,
  // since resolved ones navigate away first). Binding these behind `(hover: hover)` leaves touch with
  // a clean tap-to-open / tap-to-close toggle.
  const canHover = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover)').matches

  if (canHover) {
    container.addEventListener('mouseenter', (e) => {
      const el = citeFrom(e)
      if (el) open(targetFrom(el, { x: e.clientX, y: e.clientY }), 'pointer')
    }, true)

    container.addEventListener('mouseleave', (e) => {
      if (citeFrom(e)) scheduleClose()
    }, true)
  }

  // Keyboard focus has no pointer, so the card anchors to the line the citation starts on.
  container.addEventListener('focusin', (e) => {
    const el = citeFrom(e)
    if (el) open(targetFrom(el), 'pointer')
  })

  container.addEventListener('focusout', (e) => {
    if (citeFrom(e)) scheduleClose()
  })
}

defineExpose({ setupListeners })
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      leave-active-class="transition-all duration-100 ease-in"
      leave-to-class="opacity-0 translate-y-1"
    >
      <!-- The wrapper's padding is the hover bridge: it keeps the gap between citation and card
           hoverable, on whichever side `flip` ends up putting the card. -->
      <div
        v-if="target"
        ref="floatingRef"
        class="citation-popover z-[100] p-1.5"
        :style="floatingStyles"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <CitationCard
          :label="target.label"
          :snippet="currentSnippet"
          :loading="isLoading"
          @navigate="handleCardNavigate"
        />
      </div>
    </Transition>
  </Teleport>
</template>
